import { useState } from "react";
import { Link, useLocation } from "wouter";
import { ArrowRight, Menu } from "lucide-react";
import { useTranslation } from "@/i18n/useTranslation";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();

  const navLinks = [
    { href: "/#features", label: t("nav.features") },
    { href: "/#how", label: t("nav.howto") },
    { href: "/#pricing", label: t("nav.pricing") },
    { href: "/shop", label: t("nav.shop"), internal: true },
    { href: "/#faq", label: t("nav.faq") },
  ];

  return (
    <header className="sticky top-0 z-40 w-full bg-background/85 backdrop-blur border-b border-border/70">
      <div className="container mx-auto max-w-[1440px] px-5 md:px-6 h-16 flex items-center justify-between gap-3 md:gap-6">
        <Link href="/" className="shrink-0">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 text-[0.92rem] text-muted-foreground">
          {navLinks.map((l) =>
            l.internal ? (
              <Link key={l.href} href={l.href} className="hover:text-foreground transition-colors">
                {l.label}
              </Link>
            ) : (
              <a key={l.href} href={l.href} className="hover:text-foreground transition-colors">
                {l.label}
              </a>
            ),
          )}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-2 md:gap-3">
          <LanguageSwitcher />
          <a
            href="https://admin.cookierobot.info/login"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center text-[0.92rem] text-muted-foreground hover:text-foreground transition-colors px-2"
          >
            {t("nav.login")}
          </a>
          <a
            href="https://admin.cookierobot.info/register"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center gap-1.5 rounded-md bg-white px-3.5 py-2 text-[0.88rem] font-semibold text-foreground border border-border hover:bg-muted transition-colors"
          >
            {t("nav.signupFree")}
          </a>
          <Link
            href="/shop"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-[0.88rem] font-semibold text-primary-foreground border border-[hsl(208,71%,30%)] hover:bg-[hsl(208,71%,34%)] transition-colors"
          >
            {t("nav.signupPro")} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Mobile cluster: Pro CTA + hamburger */}
        <div className="flex md:hidden items-center gap-2">
          <Link
            href="/shop"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-[0.82rem] font-semibold text-primary-foreground border border-[hsl(208,71%,30%)]"
          >
            {t("nav.signupPro")}
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label="Menü öffnen"
                className="inline-flex items-center justify-center w-10 h-10 rounded-md border border-border bg-white text-foreground"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] sm:w-[380px] p-0 flex flex-col">
              <SheetHeader className="px-5 py-4 border-b border-border flex-row items-center justify-between space-y-0">
                <SheetTitle asChild>
                  <Link href="/" onClick={() => setOpen(false)}>
                    <Logo />
                  </Link>
                </SheetTitle>
              </SheetHeader>

              <nav className="flex-1 px-5 py-6 flex flex-col gap-1">
                {navLinks.map((l) =>
                  l.internal ? (
                    <Link
                      key={l.href}
                      href={l.href}
                      onClick={() => setOpen(false)}
                      className="px-3 py-3 rounded-md text-base font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      key={l.href}
                      href={l.href}
                      onClick={(e) => {
                        e.preventDefault();
                        const id = l.href.split("#")[1];
                        setOpen(false);
                        if (window.location.pathname !== "/") {
                          navigate("/");
                          setTimeout(() => {
                            document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
                          }, 80);
                        } else {
                          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
                        }
                      }}
                      className="px-3 py-3 rounded-md text-base font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      {l.label}
                    </a>
                  ),
                )}
              </nav>

              <div className="px-5 py-5 border-t border-border space-y-3">
                <LanguageSwitcher />
                <a
                  href="https://admin.cookierobot.info/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center rounded-md border border-border bg-white px-4 py-2.5 text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                >
                  {t("nav.login")}
                </a>
                <a
                  href="https://admin.cookierobot.info/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground border border-[hsl(208,71%,30%)] hover:bg-[hsl(208,71%,34%)] transition-colors"
                >
                  {t("nav.signupFree")}
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
